import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Container, Heading, Label, Paragraph, Select, Skeleton, Spinner } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

function PersonalNotificationAdvancedSettings(props: {
	engagementSetting: graphql.UserNotificationSetting;
}) {
	const { t } = useTranslation();
	const [update, { loading: updating }] = graphql.useUpdatePersonalNotificationSettingMutation();

	const engagementChannels = props.engagementSetting
		? Object.fromEntries(
				props.engagementSetting.channels.map(({ channel, frequency }) => [channel, frequency]),
		  )
		: {};

	const engagementFrequencyEmail = engagementChannels?.[graphql.NotificationChannel.Email];
	const engagementFrequencySlack = engagementChannels?.[graphql.NotificationChannel.Slack];

	const [engagementFrequency, setEngagementFrequency] = useState<string | undefined>(
		engagementFrequencyEmail === engagementFrequencySlack
			? 'both'
			: engagementFrequencyEmail === graphql.NotificationFrequency.Instant
			? graphql.NotificationChannel.Email
			: graphql.NotificationChannel.Slack,
	);

	return (
		<>
			<Heading mt={4}>{t('personal.notification.settings.advanced')}</Heading>
			<Paragraph mb={2}>{t('personal.notification.settings.engagement')}</Paragraph>
			<Flex alignItems="center">
				<Select
					selectedValue={engagementFrequency}
					onChange={(value) => {
						setEngagementFrequency(value);

						(async () => {
							await update({
								variables: {
									input: {
										type: 'engagement.response.created',
										channel: graphql.NotificationChannel.Email,
										frequency:
											value === graphql.NotificationChannel.Email || value === 'both'
												? graphql.NotificationFrequency.Instant
												: graphql.NotificationFrequency.Disabled,
									},
								},
							});

							await update({
								variables: {
									input: {
										type: 'engagement.response.created',
										channel: graphql.NotificationChannel.Slack,
										frequency:
											value === graphql.NotificationChannel.Slack || value === 'both'
												? graphql.NotificationFrequency.Instant
												: graphql.NotificationFrequency.Disabled,
									},
								},
							});
						})();
					}}
					options={[
						{
							title: t('personal.notification.settings.slack'),
							value: graphql.NotificationChannel.Slack,
						},
						{
							title: t('personal.notification.settings.email'),
							value: graphql.NotificationChannel.Email,
						},
						{
							title: t('personal.notification.settings.both'),
							value: 'both',
						},
					]}
					search={false}
				/>
				{updating && <Spinner ml={2} />}
			</Flex>
		</>
	);
}

export default function PersonalNotificationSettings() {
	const { t } = useTranslation();

	const query = graphql.usePersonalNotificationSettingsQuery();
	const { data: slackData } = graphql.useSlackConfigQuery();

	const [update, { loading: updating }] = graphql.useUpdatePersonalNotificationSettingMutation();

	const [changedFrequency, setChangedFrequency] = useState<
		graphql.NotificationFrequency | undefined
	>();
	const frequency =
		query.data?.viewer.notificationSettings[0]?.channels[0]?.frequency ??
		graphql.NotificationFrequency.Instant;

	const engagementSetting = query.data?.viewer.notificationSettings.find(
		(notificationSetting) => notificationSetting.type === 'engagement.response.created',
	);

	return (
		<Container py={4}>
			<Flex>
				<Box width={6 / 12}>
					<Heading>{t('personal.notification.settings.general')}</Heading>
					<Label mb={2}>{t('personalSettings.notifications.digest.label')}</Label>
					{query.data ? (
						<Flex alignItems="center">
							<Select
								search={false}
								onChange={async (frequency) => {
									setChangedFrequency(frequency as graphql.NotificationFrequency);

									const types = [
										'evaluations.submissions.pending',
										'evaluations.submissions.employee.shared',
										'evaluations.submissions.signed',
										'compliments.received',
										'feedback.response',
										'feedback.request.pending',
									];

									await Promise.all(
										types.map((type) =>
											update({
												variables: {
													input: {
														type,
														channel: graphql.NotificationChannel.Email,
														frequency: frequency as graphql.NotificationFrequency,
													},
												},
											}),
										),
									);
								}}
								selectedValue={changedFrequency ?? frequency}
								options={[
									{
										title: t('personalSettings.notifications.digest.off.label'),
										value: graphql.NotificationFrequency.Instant,
									},
									{
										title: t('personalSettings.notifications.digest.daily.label'),
										value: graphql.NotificationFrequency.Daily,
									},
									{
										title: t('personalSettings.notifications.digest.weekly.label'),
										value: graphql.NotificationFrequency.Weekly,
									},
								]}
							/>
							{updating && <Spinner ml={2} />}
						</Flex>
					) : (
						<Skeleton.Empty contents={<Spinner />} width={100} height={40}>
							<Skeleton.Box />
						</Skeleton.Empty>
					)}

					{query.data &&
						slackData?.viewer.workspace.slackConfig?.isConfigured &&
						engagementSetting && (
							<PersonalNotificationAdvancedSettings engagementSetting={engagementSetting} />
						)}
				</Box>
			</Flex>
		</Container>
	);
}
